// Tipos para la respuesta del Edge Function "tiempo"
import { supabase } from "@/integrations/supabase/client";

export type N8nWeatherAgricultura = {
  temp_min_C: number;
  temp_max_C: number;
  lluvia_mm: number;
  probabilidad_lluvia_pct: number;
  humedad_relativa_pct: number;
  viento_vel_ms: number;
  viento_rachas_ms: number;
  alerta_niebla_rocio: boolean;
};

/** Condición tal cual la reporta OpenWeather. El `id` es el código (800 = despejado). */
export type WeatherCondition = {
  id: number;
  main: string;
  description: string;
  /** Icono de OpenWeather; el sufijo d/n distingue día y noche. */
  icon: string;
};

/** El tiempo de AHORA. Es lo que se enseña en grande. */
export type N8nWeatherCurrent = {
  dt: number;
  observed_at_iso: string;
  temp: number;
  feels_like: number;
  humidity: number;
  pressure: number;
  uv_index: number;
  clouds_pct: number;
  viento_vel_ms: number;
  viento_rachas_ms: number;
  condition: WeatherCondition | null;
};

/**
 * Condición de una hora, tal y como viaja en la leyenda del bloque horario: sin el
 * sufijo d/n del icono, que lo pone el cliente según sea de día o de noche.
 */
export type N8nWeatherHourlyCondition = {
  id: number;
  main: string;
  description: string;
  /** Prefijo del icono («01».."13"); con la d o la n pegada sale un `WeatherCondition`. */
  icon_base: string;
};

/**
 * La pista HORARIA del periodo, en columnas y por índice. `time` va en hora LOCAL del
 * sitio y SIN sufijo de zona («2026-08-25T14:00»): el prefijo del string es la fecha
 * local y la hora se lee del texto — nunca con `new Date()`, que es de donde salían
 * los saltos de día.
 *
 * Ausente (`null`/`undefined`) cuando la función desplegada es anterior a esto: el
 * detalle del día se cae entonces a la ficha estática de siempre.
 */
export type N8nWeatherHourly = {
  time: string[];
  temp: (number | null)[];
  feels_like: (number | null)[];
  humidity: (number | null)[];
  /** Lluvia de ESA hora (mm), no acumulada del día. */
  precip_mm: (number | null)[];
  precip_prob_pct: (number | null)[];
  wind_ms: (number | null)[];
  uv_index: (number | null)[];
  /** Irradiancia real de esa hora (W/m²). */
  solar_Wm2: (number | null)[];
  code: (number | null)[];
  /** 1 de día, 0 de noche, `null` si el proveedor no lo dijo. */
  is_day: (0 | 1 | null)[];
  condition_legend: Record<string, N8nWeatherHourlyCondition>;
};

export type N8nWeatherDay = {
  date: string;                 // "YYYY-MM-DD" (fecha local del sitio consultado)
  /** Temperatura a las 12:00 local. No es una media: no la uses como «el tiempo de hoy». */
  avg_temp_C: number;
  uv_index: number;             // índice UV diario
  day_length_s: number;         // duración del día en segundos
  /** Amanecer y ocaso en hora LOCAL («YYYY-MM-DDTHH:mm»). De aquí sale el cielo. */
  sunrise_iso?: string | null;
  sunset_iso?: string | null;
  summary?: string | null;      // resumen en prosa de OpenWeather
  condition?: WeatherCondition | null;
  /** Radiación real (Open-Meteo). `null`/ausente cuando no hay dato: se pinta «—». */
  solar_energy_J_cm2?: number | null;
  solar_power_mean_Wm2?: number | null;
  /** Potencia de mediodía (media × π/2). La lee el widget antiguo; el nuevo, no. */
  solar_power_Wm2?: number | null;
  et0_mm?: number | null;       // evapotranspiración de referencia (mm)
  /** Alias heredado de `solar_energy_J_cm2`, para bundles antiguos. */
  solar_energy_J_m2?: number | null;
  agricultura?: N8nWeatherAgricultura;
};

export type N8nWeatherTotals = {
  total_solar_energy_J_m2?: number | null;
  total_solar_energy_J_cm2?: number | null;
  avg_solar_power_mean_Wm2?: number | null;
  total_lluvia_semana_mm?: number;
  total_et0_mm?: number | null;
};

export type N8nWeatherPeriod = {
  start_date: string;
  end_date: string;
  days: number;
};

export type N8nWeatherLocation = {
  city: string;
  lat: number;
  lon: number;
  timezone: string;
};

/**
 * El tiempo de AHORA en tramos de 15 minutos alrededor de ahora, en columnas y en hora
 * LOCAL sin zona (como `hourly`). El héroe enseña el tramo que contiene la hora de ahora
 * AL PINTAR, así que dos apps con el parte traído a horas distintas dicen lo mismo.
 */
export type N8nWeatherNowSeries = {
  time: string[];
  temp: (number | null)[];
  feels_like: (number | null)[];
  humidity: (number | null)[];
  wind_ms: (number | null)[];
  gust_ms: (number | null)[];
  code: (number | null)[];
  is_day: (0 | 1 | null)[];
  condition_legend: Record<string, N8nWeatherHourlyCondition>;
};

export type N8nWeatherPayload = {
  location: N8nWeatherLocation;
  /** Ausente si la respuesta viene de una versión antigua de la función. */
  current?: N8nWeatherCurrent | null;
  /** Ídem: sin ella el héroe usa `current`. */
  now_series?: N8nWeatherNowSeries | null;
  /** Ídem: sin ella el detalle del día se cae a la ficha estática. */
  hourly?: N8nWeatherHourly | null;
  period: N8nWeatherPeriod;
  days: N8nWeatherDay[];
  totals: N8nWeatherTotals;
  generated_at_iso: string;
};

export type N8nWeatherResponse = N8nWeatherPayload[] | N8nWeatherPayload;

export async function fetchN8nWeather(
  city?: string,
  countryCode?: string,
  lat?: number,
  lon?: number,
  lang?: string,
): Promise<N8nWeatherPayload> {
  const body: Record<string, string | number> = {};
  if (city) body.city = city;
  // El código ISO, no el nombre para enseñar: el geocoder de OpenWeather espera `ES`,
  // y con «España» devolvía cero resultados.
  if (countryCode) body.countryCode = countryCode;
  // El idioma va al parte para que la descripción del tiempo venga ya traducida.
  if (lang) body.lang = lang;
  // Cuando la ciudad viene del buscador trae coordenadas: la función se salta el
  // geocoding y va directa al parte. Seguimos mandando el nombre por si la función
  // desplegada es la de antes y aún no entiende lat/lon.
  if (typeof lat === "number" && typeof lon === "number") {
    body.lat = lat;
    body.lon = lon;
  }

  const { data, error } = await supabase.functions.invoke('tiempo', {
    body: Object.keys(body).length > 0 ? body : undefined,
  });

  if (error) {
    throw new Error(`Failed to fetch weather: ${error.message}`);
  }

  // Soportar tanto [payload] como payload directo
  let payload: any;

  if (Array.isArray(data)) {
    if (data.length === 0) {
      throw new Error('Weather response is empty');
    }
    payload = data[0];
  } else {
    payload = data;
  }

  // Manejar respuestas de error: { error: "..." }
  if (payload && typeof payload.error === 'string') {
    throw new Error(`Weather error: ${payload.error}`);
  }

  if (!payload || !Array.isArray(payload.days)) {
    throw new Error('Weather payload is invalid (missing days)');
  }

  return payload as N8nWeatherPayload;
}

/**
 * El día de HOY dentro de `days`, por FECHA local del sitio consultado — nunca
 * «el primero de la lista»: un payload cacheado que cruza la medianoche trae
 * AYER en days[0], y quien asuma la posición rotula «Hoy» el día equivocado
 * (bug real del widget, arreglado el 14/08, que el splash heredó sin arreglar).
 * Único sitio del cliente autorizado a indexar days[0] (lo vigila el guardián).
 */
export function pickToday<T extends { date: string }>(
  days: T[] | null | undefined,
  timezone?: string,
): T | null {
  if (!days || days.length === 0) return null;
  let todayIso: string;
  try {
    todayIso = new Date().toLocaleDateString("en-CA", timezone ? { timeZone: timezone } : undefined);
  } catch {
    todayIso = new Date().toLocaleDateString("en-CA");
  }
  return days.find((d) => d.date === todayIso) ?? days[0];
}
